import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, streamUrl, photoImageUrl } from "@/lib/api";
import { Music, Play, Pause, Eye, Loader2, Flame, ArrowLeft, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const resolveUrl = (u) => (!u ? u : /^https?:\/\//i.test(u) ? u : `${BACKEND}${u}`);

const fmt = (n) => {
  if (n == null) return "0";
  if (n < 1000) return String(n);
  if (n < 1000000) return `${(n / 1000).toFixed(1)}K`.replace(".0K", "K");
  return `${(n / 1000000).toFixed(1)}M`.replace(".0M", "M");
};

const durationLabel = (sec) => {
  if (!sec) return "";
  const m = Math.floor(sec / 60);
  const s = String(sec % 60).padStart(2, "0");
  return `${m}:${s}`;
};

export default function SoundDetail() {
  const { trackId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [previewOn, setPreviewOn] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    api
      .get(`/music/library/${encodeURIComponent(trackId)}/posts`)
      .then((r) => setData(r.data))
      .catch((e) => {
        if (e?.response?.status === 404) setNotFound(true);
        setData(null);
      })
      .finally(() => setLoading(false));
    // stop any preview when route changes
    return () => {
      audioRef.current?.pause();
      audioRef.current = null;
      setPreviewOn(false);
    };
  }, [trackId]);

  const togglePreview = () => {
    if (!data?.track) return;
    if (previewOn) {
      audioRef.current?.pause();
      setPreviewOn(false);
      return;
    }
    audioRef.current?.pause();
    const audio = new Audio(resolveUrl(data.track.url));
    audio.crossOrigin = "anonymous";
    audio.loop = true;
    audio.play().catch(() => toast.error("Preview blocked by browser"));
    audioRef.current = audio;
    setPreviewOn(true);
  };

  if (loading) {
    return (
      <div className="md:ml-60 min-h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 text-[#FF007F] animate-spin" />
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div className="md:ml-60 min-h-screen flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF007F] mb-3">
            // sound.not.found
          </p>
          <h2 className="font-display font-black text-3xl mb-3">
            No such sound
          </h2>
          <p className="text-white/50 text-sm mb-8">
            This track isn&apos;t in the library. Maybe it was removed.
          </p>
          <Link
            to="/discover"
            data-testid="sound-back-to-discover"
            className="inline-flex items-center gap-2 border border-[#39FF14]/60 text-[#39FF14] px-5 py-2.5 font-mono text-xs uppercase tracking-widest hover:bg-[#39FF14]/10"
          >
            <ArrowLeft className="h-4 w-4" />
            back to discover
          </Link>
        </div>
      </div>
    );
  }

  const { track, video_count, total_views, videos } = data;

  return (
    <div className="md:ml-60 min-h-screen pb-24 md:pb-12" data-testid="sound-detail-page">
      <div className="max-w-5xl mx-auto px-6 pt-10">
        <Link
          to="/discover"
          data-testid="sound-back"
          className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-white/40 hover:text-[#00FFFF] mb-6"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          discover
        </Link>

        {/* Hero card */}
        <div
          data-testid="sound-hero"
          className="relative bg-[#0A0A0C] border border-white/10 p-6 md:p-8 mb-10 overflow-hidden"
        >
          <div
            className="absolute inset-0 opacity-40 mix-blend-screen pointer-events-none"
            style={{
              backgroundImage:
                "radial-gradient(circle at 20% 30%, rgba(255,0,127,0.35), transparent 55%), radial-gradient(circle at 80% 70%, rgba(0,255,255,0.35), transparent 55%)",
            }}
          />
          <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center gap-6">
            {/* Cover */}
            <button
              type="button"
              onClick={togglePreview}
              data-testid="sound-hero-preview"
              className="relative w-32 h-32 md:w-40 md:h-40 border border-white/15 hover:border-[#FF007F]/70 bg-gradient-to-br from-[#FF007F]/30 via-black to-[#00FFFF]/25 flex items-center justify-center flex-shrink-0 group transition-all"
              title={previewOn ? "Pause" : "Preview"}
            >
              <div
                className={`h-16 w-16 border-2 flex items-center justify-center transition-all ${
                  previewOn
                    ? "border-[#FF007F] bg-[#FF007F]/20 text-[#FF007F]"
                    : "border-white/80 text-white group-hover:border-[#FF007F] group-hover:text-[#FF007F]"
                }`}
              >
                {previewOn ? (
                  <Pause className="h-7 w-7" fill="currentColor" />
                ) : (
                  <Play className="h-7 w-7" fill="currentColor" />
                )}
              </div>
              <Music className="absolute bottom-2 right-2 h-4 w-4 text-white/40" strokeWidth={1.5} />
            </button>

            {/* Meta */}
            <div className="flex-1 min-w-0 w-full">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#00FFFF] mb-2 flex items-center gap-2">
                // sound
                {track.source === "original" && (
                  <span
                    data-testid="sound-original-badge"
                    className="px-1.5 py-0.5 border border-[#FF007F] text-[#FF007F] bg-[#FF007F]/10 tracking-widest"
                  >
                    original
                  </span>
                )}
              </p>
              <h1
                data-testid="sound-title"
                className="font-display font-black text-3xl md:text-4xl mb-1 break-words"
              >
                {track.title}
              </h1>
              <p className="font-mono text-xs uppercase tracking-widest text-white/50 mb-4">
                {track.artist} · {track.genre}
                {track.duration_sec ? ` · ${durationLabel(track.duration_sec)}` : ""}
              </p>
              {track.source === "original" && track.creator_username && (
                <Link
                  to={`/u/${track.creator_username}`}
                  data-testid="sound-original-creator"
                  className="inline-flex items-center gap-2 mb-5 border border-white/15 hover:border-[#00FFFF]/60 hover:bg-[#00FFFF]/5 pl-1 pr-3 py-1 transition-all group"
                >
                  {track.creator_picture ? (
                    <img
                      src={track.creator_picture}
                      alt=""
                      className="h-6 w-6 rounded-full object-cover"
                    />
                  ) : (
                    <div className="h-6 w-6 rounded-full bg-[#00FFFF]/20 border border-[#00FFFF]/40 flex items-center justify-center text-[10px] font-display font-black text-[#00FFFF]">
                      {(track.creator_name || track.creator_username)?.[0]?.toUpperCase() || "?"}
                    </div>
                  )}
                  <span className="font-mono text-[10px] uppercase tracking-widest text-white/60 group-hover:text-[#00FFFF]">
                    made by @{track.creator_username}
                  </span>
                </Link>
              )}

              {/* Stats */}
              <div className="flex flex-wrap gap-6 mb-5">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                    posts
                  </p>
                  <p
                    data-testid="sound-post-count"
                    className="font-display font-black text-2xl text-[#39FF14]"
                  >
                    {fmt(video_count)}
                  </p>
                </div>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                    total views
                  </p>
                  <p className="font-display font-black text-2xl text-[#00FFFF]">
                    {fmt(total_views)}
                  </p>
                </div>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                    used
                  </p>
                  <p className="font-display font-black text-2xl text-[#FF007F] flex items-center gap-1.5">
                    <Flame className="h-5 w-5" strokeWidth={2.5} />
                    {fmt(track.use_count || 0)}
                  </p>
                </div>
              </div>

              <Link
                to={`/upload?mode=photo&track=${encodeURIComponent(track.id)}`}
                data-testid="sound-use-cta"
                className="inline-flex items-center gap-2 bg-[#FF007F] hover:shadow-[0_0_25px_rgba(255,0,127,0.6)] text-white font-bold uppercase tracking-widest px-6 py-3 transition-all"
              >
                <Play className="h-4 w-4" fill="currentColor" />
                use this sound
              </Link>
            </div>
          </div>
        </div>

        {/* Grid */}
        {video_count === 0 ? (
          <div className="py-16 text-center border border-white/10 bg-white/[0.02]">
            <Music className="h-10 w-10 text-white/25 mx-auto mb-4" strokeWidth={1.3} />
            <p className="font-mono text-xs uppercase tracking-widest text-white/40 mb-1">
              // be the first
            </p>
            <p className="text-white/60 text-sm mb-6">
              No posts yet with this sound. Start the trend.
            </p>
            <Link
              to={`/upload?mode=photo&track=${encodeURIComponent(track.id)}`}
              className="inline-flex items-center gap-2 border border-[#FF007F]/60 hover:bg-[#FF007F]/10 text-[#FF007F] px-5 py-2.5 font-mono text-xs uppercase tracking-widest"
            >
              <Play className="h-3.5 w-3.5" fill="currentColor" />
              use this sound
            </Link>
          </div>
        ) : (
          <>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-white/40 mb-4">
              // posts.using.this.sound
            </p>
            <div
              data-testid="sound-post-grid"
              className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2"
            >
              {videos.map((v) => (
                <Link
                  key={v.id}
                  to={`/v/${v.id}`}
                  data-testid={`sound-post-${v.id}`}
                  className="relative aspect-[9/16] bg-black overflow-hidden group border border-white/5 hover:border-[#FF007F]/50 transition-all"
                >
                  {v.is_photo_post ? (
                    <img
                      src={photoImageUrl(v.id)}
                      alt=""
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <video
                      src={streamUrl(v.id)}
                      className="w-full h-full object-cover"
                      muted
                      playsInline
                      preload="metadata"
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/80" />
                  {v.is_photo_post && (
                    <div className="absolute top-2 right-2 bg-black/60 px-1.5 py-0.5 font-mono text-[10px] text-[#FF007F] flex items-center gap-1">
                      <ImageIcon className="h-3 w-3" strokeWidth={2} />
                      {v.slide_count || 1}
                    </div>
                  )}
                  <div className="absolute top-2 left-2 flex items-center gap-1 bg-black/60 px-1.5 py-0.5 font-mono text-[10px] text-white/80">
                    <Eye className="h-3 w-3" strokeWidth={2} />
                    {fmt(v.displayed_view_count ?? v.view_count)}
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <Play className="h-8 w-8 text-white" fill="white" />
                  </div>
                  <div className="absolute bottom-2 left-2 right-2 text-xs text-white">
                    {v.author?.username && (
                      <p className="font-mono text-[10px] text-white/60 truncate">
                        @{v.author.username}
                      </p>
                    )}
                    {v.caption && (
                      <p className="truncate">{v.caption}</p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
